import { useEffect, useRef } from "react";

import Actionbar from "src/components/common/actionbar/Authactionbar";
import useUiuxStore from "src/store/useUiuxStore";
import { useMouseContent } from "src/utils/mouse/mouseContent";

import { MST4050_R01DetailDto, MST4050_R01HeaderDto } from "./Dto_MST4050_R01";
import { useMST4050_R01Store } from "./Store_MST4050_R01";
import { AgGridReact } from "ag-grid-react";
import { Table_MST4050_R01 } from "./Table_MST4050_R01";
import { Info_MST4050_R01 } from "./Info_MST4050_R01";
import { DetailTable_MST4050_R01 } from "./DetailTable_MST4050_R01";
import { SearchSection_MST4050_R01 } from "./SearchSection_MST4050_R01";
import { Content_MST4050_R01 } from "./Content_MST4050_R01";
import { Message } from "src/components/common/form/Message";

// 검색·목록·정보·입출고 상세 영역과 액션바를 조합하는 화면 컨테이너다.
export const Container_MST4050_R01 = () => {

	const {
		MST4050_R01List,
		MST4050_R01DetailList,
		selectedMST4050_R01,
	} = useMST4050_R01Store();

	const { setMessage } = useUiuxStore();

	// 좌측 약품 목록과 우측 상세 영역의 가로 분할 설정이다.
	const RESIZER = {
		code: 'MST4050_R01',
		type: 'width',
		storageKey: 'MST4050_R01-split-width',
		min: 5,
		max: 80,
		default: 35
	} as const;

	// 각 그리드와 분할 영역 DOM을 공통 리사이저 및 화면 로직에 전달한다.
	const gridRef = useRef<AgGridReact<MST4050_R01HeaderDto> | null>(null);
	const detailGridRef = useRef<AgGridReact<MST4050_R01DetailDto> | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const infoRef = useRef<HTMLDivElement>(null);
	const isResizing = useRef(false);

	const { splitValue, handleMouseDown } = useMouseContent({
		resizer: RESIZER,
		isResizing,
		containerRef,
		infoRef
	});

	// 우측 상단 Info와 하단 입출고 내역의 세로 분할 설정이다.
	const INFO_RESIZER = {
		code: 'MST4050_R01_info',
		type: 'height',
		storageKey: 'MST4050_R01-info-split-height',
		min: 5,
		max: 80,
		default: 40
	} as const;

	const infoContainerRef = useRef<HTMLDivElement>(null);
	const detailContainerRef = useRef<HTMLDivElement>(null);
	const isInfoResizing = useRef(false);

	const { splitValue: infoSplitValue, handleMouseDown: handleInfoMouseDown } = useMouseContent({
		resizer: INFO_RESIZER,
		isResizing: isInfoResizing,
		containerRef: infoContainerRef,
		infoRef: detailContainerRef
	});

	// API 처리와 액션바 이벤트는 Content 훅에서 한곳에 관리한다.
	const {
		handleExcelClick,
		handlePrintClick,
		fetchData,
		defaultMST4050_R01HeaderRow,
		defaultMST4050_R01DetailRow,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	} = Content_MST4050_R01({
		gridRef,
		detailGridRef,
		setMessage,
		containerRef,
		isResizing,
	});

	// 최초 진입 시 빈 헤더와 입출고 행을 만들어 즉시 입력 가능한 상태로 초기화한다.
	useEffect(() => {
		if (MST4050_R01List.length === 0 && MST4050_R01DetailList.length === 0) {
			handleCreateClick();
		}
	}, [MST4050_R01List, MST4050_R01DetailList, handleCreateClick]);

	return (
		<div className="content-container">
			<Actionbar
				onFetchClick={fetchData}
				onCreateClick={handleCreateClick}
				onDeleteClick={handleDeleteClick}
				onSaveClick={handleSaveClick}
				onPrintClick={handlePrintClick}
				onExcelClick={handleExcelClick}
				onInitClick={initContainerData}
			/>
			<SearchSection_MST4050_R01 />
			<div className="flex flex-1 p-[12px] gap-[4px] min-h-0" ref={containerRef}>
				<div className="h-full" style={{ width: `${splitValue}%` }}>
					<Table_MST4050_R01 gridRef={gridRef} />
				</div>
				<div
					className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
					onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleMouseDown('LR', e.nativeEvent)}
				/>
				<div className="h-full flex flex-col" style={{ width: `${100 - splitValue}%` }} ref={infoRef}>
					<div className="w-full h-full flex flex-col gap-[4px]" ref={infoContainerRef}>
						<div className="w-full" style={{ height: `${infoSplitValue}%` }}>
							<Info_MST4050_R01
								data={selectedMST4050_R01}
								defaultMST4050_R01HeaderRow={defaultMST4050_R01HeaderRow()}
							/>
						</div>
						<div
							className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
							onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleInfoMouseDown('UD', e.nativeEvent)}
						/>
						<div className="w-full min-h-0" style={{ height: `${100 - infoSplitValue}%` }} ref={detailContainerRef}>
							<DetailTable_MST4050_R01
								gridRef={detailGridRef}
								defaultMST4050_R01DetailRow={defaultMST4050_R01DetailRow()}
							/>
						</div>
					</div>
				</div>
			</div>
			<Message />
		</div>
	);
};
